import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Lightbulb, Users, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import SafeSEO from '@/components/SafeSEO';
import CtaSection from '@/components/CtaSection';

const Home = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: TrendingUp,
      title: t('home.services.investment'),
      description: t('home.services.investmentDesc'),
    },
    {
      icon: Lightbulb,
      title: t('home.services.digital'),
      description: t('home.services.digitalDesc'),
    },
    {
      icon: Users,
      title: t('home.services.realEstate'),
      description: t('home.services.realEstateDesc'),
    },
    {
      icon: Award,
      title: t('home.services.energy'),
      description: t('home.services.energyDesc'),
    },
  ];

  return (
    <>
      <SafeSEO
        title={t('home.heroTitle')}
        description={t('home.heroSubtitle')}
        url="https://consulogic-gt-com-544311.hostingersite.com"
      />

      <div className="pt-20">
        <section className="relative min-h-[90vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              className="w-full h-full object-cover"
              alt="Modern office building with professional team"
              src="https://images.unsplash.com/photo-1639776685264-9c2c55691f6f" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A5F7F]/95 to-[#0D7FA3]/80"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight"
              >
                {t('home.heroTitle')}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl text-white/90 mb-8 leading-relaxed"
              >
                {t('home.heroSubtitle')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/servicios">
                  <Button size="lg" className="bg-white text-[#0A5F7F] hover:bg-white/90">
                    {t('home.servicesBtn')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contacto">
                  <Button size="lg" variant="outline" className="border-white text-[#0A5F7F] hover:bg-white/10">
                    {t('home.contactBtn')}
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-4">
                {t('home.serviceAreaTitle')}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t('home.serviceAreaSubtitle')}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white/40 backdrop-blur-md p-8 rounded-3xl border border-white/20 shadow-xl shadow-gray-200/50 hover:shadow-2xl hover:translate-y-[-8px] transition-all duration-500 group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#0A5F7F]/5 to-transparent rounded-bl-full pointer-events-none" />
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A5F7F] to-[#0D7FA3] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#0A5F7F]/20 group-hover:rotate-6 transition-transform duration-500">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0A5F7F] mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F7F] mb-6">
                  {t('home.purposeTitle')}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {t('home.purposeText1')}
                </p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('home.purposeText2')}
                </p>
                <Link to="/sostenibilidad">
                  <Button className="bg-[#0A5F7F] hover:bg-[#0A5F7F]/90">
                    {t('home.learnMoreBtn')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-[#0A5F7F]/20 to-[#0D7FA3]/20 rounded-[2rem] blur-2xl z-0" />
                <motion.img
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="w-full h-[500px] object-cover rounded-[2rem] shadow-2xl relative z-10 border border-white/20"
                  alt="Professional female leader in modern office"
                  src="https://images.unsplash.com/photo-1590650467980-8eadfa86ff48" />
              </motion.div>
            </div>
          </div>
        </section>

        <CtaSection />
      </div>
    </>
  );
};

export default Home;